import {
  FormControl,
  FormField,
  FormRow,
  RecordForm,
} from "@/components/forms/base-record-form";
import { MultiSelectCombobox } from "@/components/ui/multi-select-combobox";
import { useRecordForm } from "@/hooks/use-record-form";
import { cn } from "@/lib/utils";

interface Company {
  id: string;
  name: string;
  uid: string;
}

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  phone: string;
  uid: string;
  full_name: string;
  avatar_initials: string;
  company: Company;
}

interface PeopleFormProps {
  companies: Company[];
  onSuccess?: (person: Person) => void;
  path: string;
  onCancel: () => void;
  /** Prefilled from what the user typed into a picker (INCO-69). */
  firstName?: string;
  lastName?: string;
}

export function PeopleForm({
  companies,
  onSuccess,
  path,
  onCancel,
  firstName = "",
  lastName = "",
}: PeopleFormProps) {
  const { data, setField, errors, isSubmitting, submit } = useRecordForm({
    path,
    param: "person",
    initial: {
      first_name: firstName,
      last_name: lastName,
      email: "",
      // The combobox is a multi-select in single-select mode, so it speaks in
      // arrays; the server wants one id. Flattened on submit, below.
      company_id: [] as string[],
    },
    onSuccess: (record) => onSuccess?.(record as unknown as Person),
    transform: (values) => ({ ...values, company_id: values.company_id[0] ?? "" }),
  });

  const companyOptions = companies.map((company) => ({
    value: company.id.toString(),
    label: company.name,
  }));

  return (
    <RecordForm
      onSubmit={submit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      submitLabel="Create person"
    >
      <FormRow>
        <FormField
          label="First name"
          name="first_name"
          autoFocus
          value={data.first_name}
          onChange={(value) => setField("first_name", value)}
          errors={errors}
        />
        <FormField
          label="Last name"
          name="last_name"
          value={data.last_name}
          onChange={(value) => setField("last_name", value)}
          errors={errors}
        />
      </FormRow>

      <FormField
        label="Email"
        name="email"
        type="email"
        value={data.email}
        onChange={(value) => setField("email", value)}
        errors={errors}
      />

      <FormControl label="Company" name="company_id" errors={errors}>
        <MultiSelectCombobox
          options={companyOptions}
          selectedValues={data.company_id}
          onSelectionChange={(values) => setField("company_id", values)}
          placeholder="Select a company…"
          searchPlaceholder="Search companies…"
          className={cn(errors.company_id && "border-destructive")}
          singleSelect={true}
        />
      </FormControl>
    </RecordForm>
  );
}
